import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import SelectInput from '../../pro/components/orderform/SelectInput';
import type { ChargeCatalogRow } from '../../../services/quote.service';
import { CHARGE_RATE_TYPES, CHASSIS_FEE_TYPES, FREE_TIME_UNITS } from '../quoteOptions';

/** One charge the user chose to add, with its resolved rate type + rate. */
export interface SelectedCharge {
  recno: number;
  ratetype: string;
  rate: number;
  /** Free-time allowance (0 = none) + unit (DAYS/HOURS). */
  freetime?: number;
  freetimeunit?: string;
  /** Additional modifier for any charge — mirrors the table's Additional. */
  chassisType?: string;
  chassisDays?: number;
}

const defaultFreeUnit = (desc: string): string =>
  desc.toUpperCase().includes('WAITING TIME') ? 'HOURS' : 'DAYS';

interface Props {
  /** Addable charges — the parent pre-filters out charges already on the list. */
  catalog: ChargeCatalogRow[];
  onAdd: (entries: SelectedCharge[]) => void;
  onClose: () => void;
}

interface RowState {
  checked: boolean;
  ratetype: string;
  rate: string;
  freeTime: string;
  freeUnit: string;
  chassisType: string;
  chassisDays: string;
}

/** A blank/unknown master rate type defaults to EXTRA — never save blank (a blank
 *  rate type silently drops the charge from the quote PDF). Mirrors V1, which
 *  pre-fills each row with the charge's own ratetype. */
const defaultTypeFor = (c: ChargeCatalogRow): string =>
  c.rateType && CHARGE_RATE_TYPES.includes(c.rateType) ? c.rateType : 'EXTRA';

/**
 * V1-style multi-select "Add Charges" popup (inline_quote.pl `editadditionalcharges`).
 * Lists every addable charge with a checkbox, a pre-filled Rate Type dropdown, a
 * pre-filled Rate (the tariff default) and the read-only Minimum Rate, so several
 * charges can be added in one pass instead of one-by-one.
 *
 * Portaled to document.body so its fixed overlay clears the drawer's transformed
 * ancestor (same reason as TariffRatesPopup).
 */
const AddChargesPopup: React.FC<Props> = ({ catalog, onAdd, onClose }) => {
  const [query, setQuery] = useState('');
  // Per-charge selection + editable rate/type, keyed by catalog recno. Seeded
  // once from the catalog defaults; edits are local until "Add" is clicked.
  const [state, setState] = useState<Record<number, RowState>>(() => {
    const init: Record<number, RowState> = {};
    for (const c of catalog) {
      init[c.recno] = {
        checked: false, ratetype: defaultTypeFor(c), rate: c.defaultRate || '',
        freeTime: '', freeUnit: defaultFreeUnit(c.description), chassisType: '', chassisDays: '',
      };
    }
    return init;
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((c) =>
      `${c.description} ${c.region}`.toLowerCase().includes(q));
  }, [catalog, query]);

  const rowOf = (recno: number): RowState =>
    state[recno] ?? { checked: false, ratetype: 'EXTRA', rate: '', freeTime: '', freeUnit: 'DAYS', chassisType: '', chassisDays: '' };

  const patch = (recno: number, p: Partial<RowState>) =>
    setState((s) => ({ ...s, [recno]: { ...rowOf(recno), ...p } }));

  // "Select all" toggles only the currently-filtered rows, so a search narrows
  // what a single click selects.
  const allFilteredChecked = filtered.length > 0 && filtered.every((c) => rowOf(c.recno).checked);
  const toggleAll = () =>
    setState((s) => {
      const next = { ...s };
      for (const c of filtered) next[c.recno] = { ...rowOf(c.recno), checked: !allFilteredChecked };
      return next;
    });

  const selectedCount = catalog.reduce((n, c) => n + (rowOf(c.recno).checked ? 1 : 0), 0);

  const submit = () => {
    const entries: SelectedCharge[] = [];
    for (const c of catalog) {
      const r = rowOf(c.recno);
      if (!r.checked) continue;
      const freeTime = Number(r.freeTime) || 0;
      entries.push({
        recno: c.recno,
        ratetype: r.ratetype || 'EXTRA',
        rate: Number(r.rate) || Number(c.defaultRate) || 0,
        freetime: freeTime,
        freetimeunit: freeTime > 0 ? (r.freeUnit || 'DAYS') : '',
        chassisType: r.chassisType,
        chassisDays: ['MIN', 'MAX'].includes(r.chassisType) ? Number(r.chassisDays) || 0 : 0,
      });
    }
    if (entries.length) onAdd(entries);
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div role="dialog" aria-label="Add Charges" className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-[92vw] max-w-[1400px] border border-slate-200 dark:border-slate-700 flex flex-col max-h-[85vh]">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-[0.875rem] font-semibold text-slate-800 dark:text-slate-100">Add Charges</h2>
            <p className="m-0 text-[0.71875rem] text-slate-500 dark:text-slate-400">Check the charges to add, adjust the rate type &amp; rate, then click Add.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-lg leading-none">×</button>
        </div>

        <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search charges…"
            aria-label="Search charges"
            className="w-full border border-slate-300 dark:border-gray-700 rounded px-2.5 py-1.5 text-[0.78125rem] bg-white dark:bg-gray-900 text-slate-900 dark:text-slate-100"
          />
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full text-[0.75rem] whitespace-nowrap">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 z-10">
              <tr className="text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[0.65625rem]">
                <th className="px-3 py-2 text-center w-10">
                  <input
                    type="checkbox"
                    checked={allFilteredChecked}
                    onChange={toggleAll}
                    aria-label="Select all charges"
                    className="cursor-pointer"
                  />
                </th>
                <th className="px-2 py-2 text-left">Description</th>
                <th className="px-2 py-2 text-left w-40">Rate Type</th>
                <th className="px-2 py-2 text-right w-24">Rate</th>
                <th className="px-2 py-2 text-left w-44">Additional</th>
                <th className="px-2 py-2 text-left w-28">Free Time</th>
                <th className="px-2 py-2 text-right w-20">Min</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400 dark:text-slate-500">
                  {catalog.length === 0 ? 'All charges are already added.' : 'No charges match your search.'}
                </td></tr>
              )}
              {filtered.map((c) => {
                const r = rowOf(c.recno);
                const min = Number(c.minimumRate) || 0;
                const rateNum = Number(r.rate) || 0;
                const belowMin = min > 0 && rateNum > 0 && rateNum < min;
                return (
                  <tr
                    key={c.recno}
                    className={`border-t border-slate-100 dark:border-slate-800 ${r.checked ? 'bg-blue-50/60 dark:bg-blue-900/20' : ''}`}
                  >
                    <td className="px-3 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={r.checked}
                        onChange={(e) => patch(c.recno, { checked: e.target.checked })}
                        aria-label={`Add ${c.description}`}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-slate-800 dark:text-slate-200">
                      {c.description}
                      {c.region && <span className="ml-1 text-slate-400 dark:text-slate-500">[{c.region}]</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      <SelectInput
                        value={r.ratetype}
                        onChange={(v) => patch(c.recno, { ratetype: v })}
                        options={CHARGE_RATE_TYPES}
                        ariaLabel={`Rate type for ${c.description}`}
                        className="w-full"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input
                        type="text"
                        value={r.rate}
                        onChange={(e) => patch(c.recno, { rate: e.target.value })}
                        aria-label={`Rate for ${c.description}`}
                        className={`w-20 border rounded px-1.5 py-1 text-[0.75rem] text-right bg-white text-slate-900 dark:bg-gray-900 dark:text-slate-100 ${belowMin ? 'border-red-500 ring-1 ring-red-300 dark:border-red-500 dark:ring-red-800' : 'border-slate-300 dark:border-gray-600'}`}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      {/* Additional Type and Days are available for every charge. */}
                      <div className="flex items-center gap-1.5">
                        <SelectInput
                          value={r.chassisType}
                          onChange={(v) => patch(c.recno, { chassisType: v })}
                          options={['', ...CHASSIS_FEE_TYPES]}
                          placeholder="—"
                          ariaLabel={`Additional type for ${c.description}`}
                          width={104}
                        />
                        {['MIN', 'MAX'].includes(r.chassisType) && (
                          <input
                            type="text"
                            value={r.chassisDays}
                            onChange={(e) => patch(c.recno, { chassisDays: e.target.value })}
                            aria-label={`Additional days for ${c.description}`}
                            placeholder="Days"
                            className="w-12 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-1 text-[0.75rem] text-right bg-white text-slate-900 dark:bg-gray-900 dark:text-slate-100"
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={r.freeTime}
                          onChange={(e) => patch(c.recno, { freeTime: e.target.value })}
                          aria-label={`Free time for ${c.description}`}
                          placeholder="0"
                          className="w-12 border border-slate-300 dark:border-gray-600 rounded px-1.5 py-1 text-[0.75rem] text-right bg-white text-slate-900 dark:bg-gray-900 dark:text-slate-100"
                        />
                        <SelectInput
                          value={r.freeUnit}
                          onChange={(v) => patch(c.recno, { freeUnit: v })}
                          options={FREE_TIME_UNITS}
                          ariaLabel={`Free time unit for ${c.description}`}
                          width={68}
                        />
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right text-slate-500 dark:text-slate-400 tabular-nums">
                      {c.minimumRate ? Number(c.minimumRate).toFixed(2) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
          <span className="text-[0.71875rem] text-slate-500 dark:text-slate-400">
            {selectedCount} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-[0.75rem] text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={selectedCount === 0}
              className="px-3 py-1.5 text-[0.75rem] font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-40 disabled:hover:bg-blue-600"
            >
              Add{selectedCount > 0 ? ` ${selectedCount}` : ''} Selected
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AddChargesPopup;
